import apiClient from './apiClient';

// Ροή δραστηριότητας και ειδοποιήσεις (P1-7).
//
// **Δικό του module υπηρεσίας**, κατά τον κανόνα των παράλληλων κλάδων: ο
// `apiClient` ανήκει στο P1-5 και κάθε κλάδος θέλει να του προσθέσει μέθοδο. Νέο
// αρχείο που **χρησιμοποιεί** τον υπάρχοντα μεταφορέα δεν συγκρούεται ποτέ.
//
// Οι διαδρομές είναι αυτές του πίνακα του §4.5.1 και καμία δεν προστέθηκε: το
// πλήθος των αδιάβαστων βγαίνει από το `meta.total` του ίδιου καταλόγου, με
// `limit=1`, αντί από χωριστό σημείο ανάγνωσης.

const query = (params) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
    }
    const text = search.toString();
    return text ? `?${text}` : '';
};

/**
 * Η ροή μιας συλλογής (ΛΑ-21).
 *
 * Το `resource` περιορίζει τη ροή σε έναν πόρο — η **απόδοση επιμέλειας** της
 * ΛΑ-23. Το `task` κάνει το ίδιο για ένα καθήκον. Δεν είναι δύο φίλτρα: δένουν το
 * **ίδιο** πεδίο του γεγονότος και ο εξυπηρετητής απορρίπτει τον συνδυασμό τους,
 * γιατί θα ήταν αντίφαση με μορφή κενού καταλόγου.
 *
 * Ίδια διαδρομή, ίδιος έλεγχος ορατότητας: δεν προστέθηκε ούτε σημείο ανάγνωσης
 * ούτε δεύτερη διατύπωση του κανόνα.
 */
export function listCollectionActivities(collectionId, { page, limit, resource, task } = {}) {
    return apiClient.getPage(
        `/api/collections/${collectionId}/activities${query({ page, limit, resource, task })}`
    );
}

/** Οι ειδοποιήσεις μου, διασυλλογικά (ΛΑ-22). */
export function listNotifications({ page, limit, unread } = {}) {
    return apiClient.getPage(`/api/notifications${query({ page, limit, unread })}`);
}

/**
 * Το πλήθος των αδιάβαστων, για τη σήμανση του μενού.
 *
 * ⚠ **Άλλαξε διαδρομή όταν η σήμανση απέκτησε ΠΕΡΙΟΔΙΚΗ ανάκτηση.** Διάβαζε το
 * `meta.total` του καταλόγου με `limit=1` — σωστό όσο καλούνταν **μία φορά ανά
 * φόρτωση σελίδας**, και σπάταλο ανά δέκα δευτερόλεπτα: εκείνο το ερώτημα
 * ταξινομεί και ομαδοποιεί **ολόκληρο** το σύνολο ειδοποιήσεων του χρήστη, κάνει
 * αναζήτηση δράστη και προβολή, για να παραχθεί ένας ακέραιος.
 *
 * **Αποτυχία εδώ ΔΕΝ επιστρέφει σιωπηλά μηδέν πλέον.** Το μηδέν ήταν σωστό για
 * μία κλήση —η σήμανση είναι διακοσμητικό του κελύφους και δεν πρέπει να ρίξει
 * την οθόνη— αλλά σε **βρόχο** κρύβει το μόνο πράγμα που ο βρόχος οφείλει να
 * μάθει: ότι το διακριτικό έληξε. Το σφάλμα ταξιδεύει, και ο καλών αποφασίζει.
 */
/**
 * Οι **δύο** μετρητές του κελύφους, από **μία** ανάγνωση.
 *
 * Επιστρέφει πάντοτε αντικείμενο με **και τα δύο** πεδία, ακόμη και όταν το API
 * στείλει το ένα: μερική απάντηση που αφήνει πεδίο `undefined` θα κατέληγε σε
 * σήμανση που εξαφανίζεται αντί για σήμανση που δείχνει μηδέν.
 */
export async function countBadges() {
    const data = await apiClient.get('/api/notifications/unread-count');
    return {
        unread: data?.unread ?? 0,
        activeTasks: data?.activeTasks ?? 0,
        // **Χωρίς προεπιλογή `false`.** Ένα `?? false` θα υποβίβαζε σιωπηλά τον
        // διαχειριστή σε κάθε μερική απάντηση — δηλαδή θα έκρυβε το μενού του
        // από σφάλμα μορφής. Το `undefined` δεν αλλάζει τίποτα στη συνεδρία.
        isAdmin: data?.isAdmin
    };
}

export async function countUnread() {
    // ⚠ **Ο `get` επιστρέφει ΗΔΗ το `data`.** Η πρώτη γραφή έκανε
    // `const { data } = await apiClient.get(...)`, δηλαδή ξετύλιγε **δεύτερη
    // φορά** κάτι που ήταν ήδη ξετυλιγμένο: το αποτέλεσμα ήταν `undefined`, το
    // `?? 0` το έκανε μηδέν, και η σήμανση του μενού **εξαφανίστηκε** — χωρίς
    // κανένα σφάλμα, γιατί το μηδέν είναι έγκυρο πλήθος.
    const data = await apiClient.get('/api/notifications/unread-count');
    return data?.unread ?? 0;
}

/**
 * ΛΑ-22: **μαζική** σήμανση ως αναγνωσμένων.
 *
 * Επιστρέφει πόσες σημειώθηκαν, ώστε η οθόνη να το πει: μαζική ενέργεια που δεν
 * αναφέρει μέγεθος αφήνει τον χρήστη να μαντεύει τι έκανε.
 */
export async function markAllRead() {
    const data = await apiClient.post('/api/notifications/read-all', {});
    return data?.marked ?? 0;
}

/**
 * Σήμανση ως αναγνωσμένης (ΛΑ-22).
 *
 * Το `id` είναι ο **αντιπρόσωπος** της ομάδας που έδωσε ο κατάλογος, και ο
 * εξυπηρετητής σημειώνει ολόκληρη την ομάδα: η μονάδα της ενέργειας ακολουθεί τη
 * μονάδα της οθόνης, όπου μία ενέργεια είναι μία γραμμή.
 */
export function markRead(id) {
    return apiClient.patch(`/api/notifications/${id}/read`);
}

export default { listCollectionActivities, listNotifications, countBadges, countUnread, markAllRead, markRead };
